import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
  ColumnFiltersState,
  SortingState,
} from '@tanstack/react-table';
import { GuestGroup } from '../types/interfaces';
import {
  computeGiftValueEur,
  computeGiftValueIls,
  deriveGroupSide,
  formatCompactGift,
  formatMoney,
  groupHasSplitGifts,
  groupThankedStatus,
  GroupThankedStatus,
  hasGift,
  shouldShowGroupInGiftsTable,
  sumGroupGiftEur,
  sumGroupGiftIls,
} from '../utils/gifts';
import { fallbackEurRates, type EurRates } from '../utils/exchangeRate';

type AttendanceLabel = 'Coming' | 'Not coming';
type InvitedLabel = 'Invited' | 'Not invited';
type PhysicalLabel = 'Yes' | '—';
type GiftGivenLabel = 'Yes' | 'No';

export interface GiftMemberLine {
  name: string;
  attendance: AttendanceLabel;
  amountNote?: string;
}

export interface GiftRow {
  group: GuestGroup;
  groupName: string;
  members: GiftMemberLine[];
  attendances: AttendanceLabel[];
  invited: InvitedLabel;
  side: string;
  giftEur: number;
  giftIls: number;
  giftValueEur: number;
  giftValueIls: number;
  physicalLabel: PhysicalLabel;
  giftGiven: GiftGivenLabel;
  thanked: GroupThankedStatus;
}

type DisplayGiftRow = GiftRow;

const ATTENDANCE_FILTER_ORDER: AttendanceLabel[] = ['Coming', 'Not coming'];
const INVITED_FILTER_ORDER: InvitedLabel[] = ['Invited', 'Not invited'];
const PHYSICAL_FILTER_ORDER: PhysicalLabel[] = ['Yes', '—'];
const THANKED_FILTER_ORDER: GroupThankedStatus[] = ['Yes', 'No', 'Partial', '—'];
const GIFT_GIVEN_FILTER_ORDER: GiftGivenLabel[] = ['Yes', 'No'];
const SIDE_FILTER_ORDER = ['Sara', 'Gavriel', 'Both'] as const;

function attendanceLabel(coming: boolean | undefined): AttendanceLabel {
  if (coming === true) return 'Coming';
  return 'Not coming';
}

function invitedLabel(group: GuestGroup): InvitedLabel {
  if (group.giftOnly || !group.rsvpCode || !group.language) return 'Not invited';
  return 'Invited';
}

export function buildGiftRows(
  groups: GuestGroup[],
  rates?: Pick<EurRates, 'ilsPerEur'> | null,
): GiftRow[] {
  const ilsPerEur = rates?.ilsPerEur ?? fallbackEurRates().ilsPerEur;
  const rows: GiftRow[] = [];

  for (const group of groups) {
    if (!shouldShowGroupInGiftsTable(group.guests)) continue;

    const split = groupHasSplitGifts(group.guests);
    const giftEur = sumGroupGiftEur(group.guests);
    const giftIls = sumGroupGiftIls(group.guests);

    const members: GiftMemberLine[] = group.guests.map(guest => ({
      name: guest.fullName,
      attendance: attendanceLabel(guest.coming),
      amountNote: split ? formatCompactGift(guest) : undefined,
    }));

    const giftEurOpt = giftEur || undefined;
    const giftIlsOpt = giftIls || undefined;
    rows.push({
      group,
      groupName: group.groupInvite,
      members,
      attendances: members.map(m => m.attendance),
      side: deriveGroupSide(group.guests),
      invited: invitedLabel(group),
      giftEur,
      giftIls,
      giftValueEur: computeGiftValueEur(giftEurOpt, giftIlsOpt, ilsPerEur),
      giftValueIls: computeGiftValueIls(giftEurOpt, giftIlsOpt, ilsPerEur),
      physicalLabel: group.guests.some(g => g.giftPhysical === true) ? 'Yes' : '—',
      giftGiven: group.guests.some(hasGift) ? 'Yes' : 'No',
      thanked: groupThankedStatus(group.guests),
    });
  }

  return rows;
}

export interface GiftTotals {
  /** Sum of amounts entered in EUR column */
  cashEur: number;
  /** Sum of amounts entered in ILS column */
  cashIls: number;
  /** All gifts converted to EUR */
  totalEur: number;
  /** All gifts converted to USD */
  totalUsd: number;
  /** All gifts converted to ILS */
  totalIls: number;
  physicalCount: number;
  notThankedCount: number;
  rowCount: number;
}

export function computeGiftTotals(
  rows: GiftRow[],
  rates?: Pick<EurRates, 'ilsPerEur' | 'usdPerEur'> | null,
): GiftTotals {
  const resolved = rates ?? fallbackEurRates();
  let cashEur = 0;
  let cashIls = 0;
  let physicalCount = 0;
  let notThankedCount = 0;

  for (const row of rows) {
    cashEur += row.giftEur;
    cashIls += row.giftIls;
    if (row.physicalLabel === 'Yes') physicalCount++;
    const hasUnthankedGift = row.giftValueEur > 0 || row.physicalLabel === 'Yes';
    if (hasUnthankedGift && row.thanked !== 'Yes') notThankedCount++;
  }

  const totalEur = rows.reduce((s, r) => s + r.giftValueEur, 0);

  return {
    cashEur,
    cashIls,
    totalEur,
    totalUsd: totalEur * resolved.usdPerEur,
    totalIls: totalEur * resolved.ilsPerEur,
    physicalCount,
    notThankedCount,
    rowCount: rows.length,
  };
}

function getFilterValues(columnFilters: ColumnFiltersState, id: string): string[] | undefined {
  const value = columnFilters.find(f => f.id === id)?.value;
  if (!value || !Array.isArray(value) || value.length === 0) return undefined;
  return value as string[];
}

function MultiColumnFilter({
  column,
  rows,
  fixedOptions,
}: {
  column: { id: string; getFilterValue: () => unknown; setFilterValue: (v: undefined | string[]) => void };
  rows: GiftRow[];
  fixedOptions?: readonly string[];
}) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const options = useMemo(() => {
    if (fixedOptions) return [...fixedOptions];
    const vals = new Set<string>();
    rows.forEach(row => {
      const val = row[column.id as keyof GiftRow];
      if (typeof val === 'string' && val && val !== '—') vals.add(val);
    });
    return [...vals].sort();
  }, [rows, column.id, fixedOptions]);

  const selected = (column.getFilterValue() as string[] | undefined) ?? [];

  const toggle = (val: string) => {
    const next = selected.includes(val)
      ? selected.filter(s => s !== val)
      : [...selected, val];
    column.setFilterValue(next.length ? next : undefined);
  };

  const updateMenuPosition = () => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const menuWidth = 144;
    const left = Math.min(rect.left, window.innerWidth - menuWidth - 8);
    setMenuPos({ top: rect.bottom + 4, left: Math.max(8, left) });
  };

  const closeMenu = () => {
    setOpen(false);
    setMenuPos(null);
  };

  useEffect(() => {
    if (!open) return;
    updateMenuPosition();
    const onReposition = () => updateMenuPosition();
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
    return () => {
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (buttonRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      closeMenu();
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  if (options.length <= 1) {
    return <span className="inline-block w-5 h-5 shrink-0" aria-hidden />;
  }

  const menu =
    open &&
    menuPos &&
    createPortal(
      <div
        ref={menuRef}
        role="listbox"
        className="min-w-[9rem] max-h-48 overflow-y-auto border border-gray-200 rounded-md bg-white p-2 space-y-1.5 shadow-xl"
        style={{
          position: 'fixed',
          top: menuPos.top,
          left: menuPos.left,
          zIndex: 10000,
        }}
      >
        {options.map(opt => (
          <label
            key={opt}
            className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer whitespace-nowrap hover:bg-gray-50 rounded px-1 py-0.5"
          >
            <input
              type="checkbox"
              className="rounded border-gray-300 shrink-0"
              checked={selected.includes(opt)}
              onChange={() => toggle(opt)}
            />
            <span>{opt}</span>
          </label>
        ))}
      </div>,
      document.body,
    );

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={e => {
          e.stopPropagation();
          if (open) {
            closeMenu();
          } else {
            updateMenuPosition();
            setOpen(true);
          }
        }}
        className={`inline-flex items-center justify-center w-5 h-5 shrink-0 rounded text-[10px] leading-none border transition-colors ${
          selected.length > 0
            ? 'bg-gray-700 text-white border-gray-700'
            : 'bg-white text-gray-500 border-gray-300 hover:border-gray-400 hover:text-gray-700'
        }`}
        aria-label="Filter column"
        aria-expanded={open}
      >
        ▾
      </button>
      {menu}
    </>
  );
}

/** Fixed column widths — paired with table-fixed to prevent layout shift when filtering. */
const GIFT_COLUMN_LAYOUT: Record<string, string> = {
  groupName: 'w-[5.5rem] max-w-[5.5rem]',
  members: 'w-[6.75rem] max-w-[6.75rem]',
  side: 'w-[3.25rem] max-w-[3.25rem]',
  invited: 'w-[4.75rem] max-w-[4.75rem]',
  giftGiven: 'w-[3.5rem] max-w-[3.5rem]',
  attendances: 'w-[5.25rem] max-w-[5.25rem]',
  physicalLabel: 'w-[3.5rem] max-w-[3.5rem]',
  thanked: 'w-[4.25rem] max-w-[4.25rem]',
  giftEur: 'w-[3.75rem] max-w-[3.75rem]',
  giftIls: 'w-[3.75rem] max-w-[3.75rem]',
  giftValueEur: 'w-[4.25rem] max-w-[4.25rem]',
  giftValueIls: 'w-[4.25rem] max-w-[4.25rem]',
};

function columnLayout(columnId: string): string {
  return GIFT_COLUMN_LAYOUT[columnId] ?? '';
}

function columnDivider(isLast: boolean, inHeader = false): string {
  if (isLast) return '';
  return inHeader ? 'border-r border-gray-300' : 'border-r border-gray-200';
}

const AttendanceList = ({ members }: { members: GiftMemberLine[] }) => (
  <ul className="list-disc list-inside m-0 p-0 space-y-0.5">
    {members.map(m => {
      const cls = m.attendance === 'Coming' ? 'text-emerald-700' : 'text-rose-600';
      return (
        <li key={m.name} className={`text-xs leading-snug ${cls}`}>
          {m.attendance}
        </li>
      );
    })}
  </ul>
);

const GuestList = ({ members }: { members: GiftMemberLine[] }) => (
  <ul className="list-disc list-inside m-0 p-0 space-y-0.5">
    {members.map(m => (
      <li key={m.name} className="text-xs text-gray-800 leading-snug break-words">
        {m.name}
        {m.amountNote && (
          <span className="text-gray-400 font-normal block">({m.amountNote})</span>
        )}
      </li>
    ))}
  </ul>
);

interface GiftTableProps {
  guestGroups: GuestGroup[];
  rates: EurRates;
}

const columnHelper = createColumnHelper<DisplayGiftRow>();

const GiftTable: React.FC<GiftTableProps> = ({ guestGroups, rates: ratesProp }) => {
  const [sorting, setSorting] = useState<SortingState>([{ id: 'giftValueEur', desc: true }]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const rates = ratesProp ?? fallbackEurRates();
  const data = useMemo(() => buildGiftRows(guestGroups, rates), [guestGroups, rates]);

  const filteredData = useMemo<DisplayGiftRow[]>(() => {
    const invitedFilter = getFilterValues(columnFilters, 'invited');
    const giftGivenFilter = getFilterValues(columnFilters, 'giftGiven');
    const thankedFilter = getFilterValues(columnFilters, 'thanked');
    const physicalFilter = getFilterValues(columnFilters, 'physicalLabel');
    const attendanceFilter = getFilterValues(columnFilters, 'attendances');
    const sideFilter = getFilterValues(columnFilters, 'side');

    const result: DisplayGiftRow[] = [];

    for (const row of data) {
      if (invitedFilter && !invitedFilter.includes(row.invited)) continue;
      if (giftGivenFilter && !giftGivenFilter.includes(row.giftGiven)) continue;
      if (thankedFilter && !thankedFilter.includes(row.thanked)) continue;
      if (physicalFilter && !physicalFilter.includes(row.physicalLabel)) continue;
      if (sideFilter && !sideFilter.includes(row.side)) continue;

      if (attendanceFilter && !row.attendances.some(a => attendanceFilter.includes(a))) {
        continue;
      }

      result.push(row);
    }

    return result;
  }, [data, columnFilters]);

  const columns = useMemo(
    () => [
      columnHelper.accessor('groupName', {
        header: 'Group',
        enableColumnFilter: false,
        cell: info => (
          <span className="block text-xs leading-snug break-words whitespace-normal">
            {info.getValue()}
          </span>
        ),
      }),
      columnHelper.accessor('members', {
        header: 'Guests',
        enableSorting: false,
        enableColumnFilter: false,
        cell: info => <GuestList members={info.getValue()} />,
      }),
      columnHelper.accessor('side', {
        header: 'Side',
        cell: info => {
          const v = info.getValue();
          if (v === '-') return <span className="text-gray-400 text-xs">—</span>;
          return <span className="text-gray-800 text-xs leading-tight">{v}</span>;
        },
      }),
      columnHelper.accessor('invited', {
        header: 'Invited',
        cell: info => {
          const v = info.getValue();
          const cls = v === 'Invited' ? 'text-emerald-700' : 'text-rose-600';
          return <span className={`text-xs ${cls}`}>{v}</span>;
        },
      }),
      columnHelper.accessor('giftGiven', {
        header: 'Gift',
        cell: info => {
          const v = info.getValue();
          const cls = v === 'Yes' ? 'text-emerald-700' : 'text-gray-500';
          return <span className={`text-xs ${cls}`}>{v === 'Yes' ? 'Yes' : 'No'}</span>;
        },
      }),
      columnHelper.accessor('attendances', {
        header: 'Attend.',
        enableSorting: false,
        cell: info => <AttendanceList members={info.row.original.members} />,
      }),
      columnHelper.accessor('physicalLabel', {
        id: 'physicalLabel',
        header: 'Phys.',
        cell: info => (
          <span className="text-xs">{info.getValue() === 'Yes' ? 'Yes' : '—'}</span>
        ),
      }),
      columnHelper.accessor('thanked', {
        header: 'Thanks',
        cell: info => {
          const v = info.getValue();
          if (v === 'Yes') return <span className="text-emerald-700 text-xs">Yes</span>;
          if (v === 'No') return <span className="text-rose-600 text-xs">No</span>;
          if (v === 'Partial') return <span className="text-amber-600 text-xs">Part.</span>;
          return <span className="text-gray-400 text-xs">—</span>;
        },
      }),
      columnHelper.accessor('giftEur', {
        header: 'EUR',
        enableColumnFilter: false,
        cell: info => <span className="text-xs tabular-nums">{formatMoney(info.getValue() || undefined, 'EUR')}</span>,
      }),
      columnHelper.accessor('giftIls', {
        header: 'ILS',
        enableColumnFilter: false,
        cell: info => <span className="text-xs tabular-nums">{formatMoney(info.getValue() || undefined, 'ILS')}</span>,
      }),
      columnHelper.accessor('giftValueEur', {
        header: 'Σ €',
        enableColumnFilter: false,
        cell: info => <span className="text-xs tabular-nums">{formatMoney(info.getValue() || undefined, 'EUR')}</span>,
      }),
      columnHelper.accessor('giftValueIls', {
        header: 'Σ ₪',
        enableColumnFilter: false,
        cell: info => <span className="text-xs tabular-nums">{formatMoney(info.getValue() || undefined, 'ILS')}</span>,
      }),
    ],
    [],
  );

  const table = useReactTable({
    data: filteredData,
    columns,
    state: { sorting, columnFilters },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const activeFilterCount = columnFilters.filter(f => {
    const v = f.value;
    if (v === undefined || v === null) return false;
    if (Array.isArray(v)) return v.length > 0;
    return true;
  }).length;

  const visibleRows = table.getRowModel().rows;
  const totals = useMemo(
    () => computeGiftTotals(visibleRows.map(r => r.original), rates),
    [visibleRows, rates],
  );
  const totalGroupCount = data.length;
  const visibleGroupCount = visibleRows.length;

  const countDisplay =
    visibleGroupCount === totalGroupCount
      ? `${totalGroupCount} groups (with someone coming or a gift)`
      : `Showing ${visibleGroupCount} of ${totalGroupCount} groups`;

  const filterableColumnOptions: Record<string, readonly string[] | undefined> = {
    side: SIDE_FILTER_ORDER,
    invited: INVITED_FILTER_ORDER,
    giftGiven: GIFT_GIVEN_FILTER_ORDER,
    attendances: ATTENDANCE_FILTER_ORDER,
    physicalLabel: PHYSICAL_FILTER_ORDER,
    thanked: THANKED_FILTER_ORDER,
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3">
        <div className="bg-gray-50 p-4 rounded text-center min-h-[5.5rem]">
          <h3 className="text-gray-500 text-xs uppercase tracking-wide mb-1">Total (€)</h3>
          <p className="text-2xl font-bold m-0 text-gray-800 tabular-nums">
            {formatMoney(totals.totalEur, 'EUR')}
          </p>
          <p className="text-xs text-gray-400 m-0 mt-1">all gifts converted</p>
        </div>
        <div className="bg-gray-50 p-4 rounded text-center">
          <h3 className="text-gray-500 text-xs uppercase tracking-wide mb-1">Total ($)</h3>
          <p className="text-2xl font-bold m-0 text-gray-800">
            {formatMoney(totals.totalUsd, 'USD')}
          </p>
          <p className="text-xs text-gray-400 m-0 mt-1">all gifts converted</p>
        </div>
        <div className="bg-gray-50 p-4 rounded text-center">
          <h3 className="text-gray-500 text-xs uppercase tracking-wide mb-1">Total (₪)</h3>
          <p className="text-2xl font-bold m-0 text-gray-800">
            {formatMoney(totals.totalIls, 'ILS')}
          </p>
          <p className="text-xs text-gray-400 m-0 mt-1">all gifts converted</p>
        </div>
        <div className="bg-gray-50 p-4 rounded text-center">
          <h3 className="text-gray-500 text-xs uppercase tracking-wide mb-1">Cash (€)</h3>
          <p className="text-xl font-bold m-0 text-gray-800">{formatMoney(totals.cashEur, 'EUR')}</p>
          <p className="text-xs text-gray-400 m-0 mt-1">EUR column only</p>
        </div>
        <div className="bg-gray-50 p-4 rounded text-center">
          <h3 className="text-gray-500 text-xs uppercase tracking-wide mb-1">Cash (₪)</h3>
          <p className="text-xl font-bold m-0 text-gray-800">{formatMoney(totals.cashIls, 'ILS')}</p>
          <p className="text-xs text-gray-400 m-0 mt-1">ILS column only</p>
        </div>
        <div className="bg-gray-50 p-4 rounded text-center">
          <h3 className="text-gray-500 text-xs uppercase tracking-wide mb-1">Physical gifts</h3>
          <p className="text-2xl font-bold m-0 text-gray-800">{totals.physicalCount}</p>
        </div>
        <div className="bg-gray-50 p-4 rounded text-center">
          <h3 className="text-gray-500 text-xs uppercase tracking-wide mb-1">Not thanked</h3>
          <p className="text-2xl font-bold m-0 text-rose-600">{totals.notThankedCount}</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 min-h-[2.25rem]">
        <p className="text-sm text-gray-500 m-0 tabular-nums">{countDisplay}</p>
        <div className="flex flex-wrap items-center gap-3 min-h-[2rem]">
          <button
            type="button"
            onClick={() => setColumnFilters([])}
            disabled={activeFilterCount === 0}
            className={`text-sm px-3 py-1.5 rounded border transition-colors duration-200 ${
              activeFilterCount > 0
                ? 'cursor-pointer border-red-300 text-red-600 hover:bg-red-50'
                : 'invisible border-transparent'
            }`}
            aria-hidden={activeFilterCount === 0}
            tabIndex={activeFilterCount === 0 ? -1 : 0}
          >
            Clear filters ({activeFilterCount || 0})
          </button>
        </div>
      </div>

      <div className="overflow-x-auto [scrollbar-gutter:stable]">
        <table className="w-full min-w-[48rem] table-fixed border-collapse text-sm text-left bg-white border border-gray-200 rounded-md overflow-hidden">
          <colgroup>
            {table.getHeaderGroups()[0]?.headers.map(header => (
              <col key={header.id} className={columnLayout(header.column.id)} />
            ))}
          </colgroup>
          <thead className="relative z-10">
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id} className="bg-gray-50">
                {headerGroup.headers.map((header, headerIndex, headers) => {
                  const isLast = headerIndex === headers.length - 1;
                  const title = flexRender(header.column.columnDef.header, header.getContext());
                  const fixedOptions = filterableColumnOptions[header.column.id];
                  const sorted = header.column.getIsSorted();
                  const sortMark = sorted === 'asc' ? '↑' : sorted === 'desc' ? '↓' : '';
                  return (
                    <th
                      key={header.id}
                      className={`py-2.5 px-1.5 text-xs font-semibold text-gray-700 align-top border-b-2 border-gray-300 ${columnDivider(isLast, true)} ${columnLayout(header.column.id)}`}
                    >
                      <div className="flex items-start gap-0.5">
                        <span
                          className={`flex-1 min-w-0 leading-tight break-words ${
                            header.column.getCanSort() ? 'cursor-pointer select-none' : ''
                          }`}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {title}
                          <span className="inline-block w-3 text-center text-[10px] opacity-70">
                            {sortMark || '\u00a0'}
                          </span>
                        </span>
                        {header.column.getCanFilter() ? (
                          <MultiColumnFilter
                            column={header.column}
                            rows={data}
                            fixedOptions={fixedOptions}
                          />
                        ) : (
                          <span className="inline-block w-5 h-5 shrink-0" aria-hidden />
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {visibleRows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="py-8 text-center text-gray-500">
                  No groups match. Run <code className="text-xs bg-gray-100 px-1 rounded">npm run sync</code>{' '}
                  after updating the sheet.
                </td>
              </tr>
            ) : (
              visibleRows.map(row => (
                <tr key={row.original.group.id} className="hover:bg-gray-50">
                  {row.getVisibleCells().map((cell, cellIndex, cells) => (
                    <td
                      key={cell.id}
                      className={`py-2 px-1.5 text-gray-800 align-top overflow-hidden border-b border-gray-100 ${columnDivider(cellIndex === cells.length - 1)} ${columnLayout(cell.column.id)}`}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default GiftTable;
